import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { API } from "../config/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  FileText, 
  ExternalLink,
  CheckSquare,
  CalendarClock,
  ClipboardList,
  Gavel
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, parseISO, isToday, addWeeks } from "date-fns";
import { ro } from "date-fns/locale";


// Event type configuration
const EVENT_TYPES = {
  court: { 
    label: "Termene Judecată", 
    icon: Gavel, 
    color: "bg-orange-500", 
    badgeColor: "bg-orange-500/10 text-orange-600 border-orange-200" 
  },
  task: { 
    label: "Sarcini", 
    icon: CheckSquare, 
    color: "bg-blue-500", 
    badgeColor: "bg-blue-500/10 text-blue-600 border-blue-200" 
  },
  appointment: { 
    label: "Programări", 
    icon: CalendarClock, 
    color: "bg-green-500", 
    badgeColor: "bg-green-500/10 text-green-600 border-green-200" 
  },
  admin_note: { 
    label: "Sarcini Personale", 
    icon: ClipboardList, 
    color: "bg-purple-500", 
    badgeColor: "bg-purple-500/10 text-purple-600 border-purple-200" 
  },
};

// Color palette for collaborators (appointments)
const COLLABORATOR_COLORS = [
  { bg: "bg-emerald-500", badge: "bg-emerald-500/10 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  { bg: "bg-cyan-500", badge: "bg-cyan-500/10 text-cyan-700 border-cyan-200", dot: "bg-cyan-500" },
  { bg: "bg-violet-500", badge: "bg-violet-500/10 text-violet-700 border-violet-200", dot: "bg-violet-500" },
  { bg: "bg-pink-500", badge: "bg-pink-500/10 text-pink-700 border-pink-200", dot: "bg-pink-500" },
  { bg: "bg-amber-500", badge: "bg-amber-500/10 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  { bg: "bg-teal-600", badge: "bg-teal-600/10 text-teal-700 border-teal-200", dot: "bg-teal-600" },
  { bg: "bg-rose-500", badge: "bg-rose-500/10 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  { bg: "bg-indigo-500", badge: "bg-indigo-500/10 text-indigo-700 border-indigo-200", dot: "bg-indigo-500" },
  { bg: "bg-lime-500", badge: "bg-lime-500/10 text-lime-700 border-lime-200", dot: "bg-lime-500" },
  { bg: "bg-fuchsia-500", badge: "bg-fuchsia-500/10 text-fuchsia-700 border-fuchsia-200", dot: "bg-fuchsia-500" },
];


export default function CalendarPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState("month");
  const [loadedRange, setLoadedRange] = useState({ start: null, end: null });
  
  // Filter states
  const [showCourt, setShowCourt] = useState(true);
  const [showTasks, setShowTasks] = useState(true);
  const [showAppointments, setShowAppointments] = useState(true);
  const [showAdminNotes, setShowAdminNotes] = useState(true);
  
  // Collaborator colors mapping (assigned_to_id -> color index)
  const [collaboratorColorMap, setCollaboratorColorMap] = useState({});

  // Build collaborator color map from appointments
  const buildCollaboratorColorMap = useCallback((allEvents) => {
    const appointments = allEvents.filter(e => e.type === "appointment");
    const uniqueAssignees = [...new Set(appointments.map(a => a.assigned_to_id).filter(Boolean))];
    
    const newMap = {};
    uniqueAssignees.forEach((id, index) => {
      newMap[id] = index % COLLABORATOR_COLORS.length;
    });
    
    setCollaboratorColorMap(newMap);
  }, []);

  // Get color for an appointment based on assigned_to_id
  const getAppointmentColor = useCallback((assignedToId) => {
    if (!assignedToId || collaboratorColorMap[assignedToId] === undefined) {
      return COLLABORATOR_COLORS[0]; // Default color
    }
    return COLLABORATOR_COLORS[collaboratorColorMap[assignedToId]];
  }, [collaboratorColorMap]);

  // Get unique collaborators with their colors for legend
  const getCollaboratorsLegend = useCallback(() => {
    const appointments = events.filter(e => e.type === "appointment");
    const collaborators = {};
    
    appointments.forEach(apt => {
      if (apt.assigned_to_id && apt.assigned_to_name && !collaborators[apt.assigned_to_id]) {
        collaborators[apt.assigned_to_id] = {
          id: apt.assigned_to_id,
          name: apt.assigned_to_name,
          color: getAppointmentColor(apt.assigned_to_id)
        };
      }
    });
    
    return Object.values(collaborators);
  }, [events, getAppointmentColor]);

  const fetchEvents = useCallback(async (forceReload = false) => {
    // Calculate range: 3 months before and 6 months after current date
    const rangeStart = startOfMonth(subMonths(new Date(), 3));
    const rangeEnd = endOfMonth(addMonths(new Date(), 6));
    
    // Check if we already have data for this range
    if (!forceReload && loadedRange.start && loadedRange.end) {
      const currentStart = startOfMonth(currentMonth);
      const currentEnd = endOfMonth(currentMonth);
      if (currentStart >= loadedRange.start && currentEnd <= loadedRange.end) {
        // Data already loaded, no need to fetch
        return;
      }
    }
    
    try {
      // Only show loading on initial load
      if (!loadedRange.start) {
        setLoading(true);
      }
      
      const start = format(rangeStart, "yyyy-MM-dd");
      const end = format(rangeEnd, "yyyy-MM-dd");
      
      // Fetch all events from the unified endpoint
      const response = await axios.get(
        `${API}/calendar/events?start_date=${start}&end_date=${end}`,
        getAuthHeader()
      );
      
      let allEvents = response.data;
      
      // Fetch admin notes if user is admin
      if (user?.role === "admin") {
        try {
          const adminNotesRes = await axios.get(
            `${API}/calendar/admin-notes?start_date=${start}&end_date=${end}`,
            getAuthHeader()
          );
          allEvents = [...allEvents, ...adminNotesRes.data];
        } catch (e) {
          console.log("Admin notes not available");
        }
      }
      
      setEvents(allEvents);
      buildCollaboratorColorMap(allEvents);
      setLoadedRange({ start: rangeStart, end: rangeEnd });
    } catch (error) {
      toast.error("Eroare la încărcarea evenimentelor");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, currentMonth, user?.role, loadedRange, buildCollaboratorColorMap]);

  // Initial load only
  useEffect(() => {
    fetchEvents();
  }, [user?.role]); // Only reload when user role changes, not on every month change

  // Filter events based on type checkboxes
  const filteredEvents = events.filter((event) => {
    if (event.type === "court" && !showCourt) return false;
    if (event.type === "task" && !showTasks) return false;
    if (event.type === "appointment" && !showAppointments) return false;
    if (event.type === "admin_note" && !showAdminNotes) return false;
    return true;
  });

  const getDatesWithEvents = () => {
    const dates = {};
    filteredEvents.forEach((event) => {
      if (!dates[event.date]) {
        dates[event.date] = [];
      }
      dates[event.date].push(event);
    });
    return dates;
  };

  const datesWithEvents = getDatesWithEvents();

  const getEventsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return datesWithEvents[dateStr] || [];
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  // Events for next 4 weeks
  const fourWeeksFromNow = format(addWeeks(new Date(), 4), "yyyy-MM-dd");
  const todayStr = format(new Date(), "yyyy-MM-dd");
  
  const upcomingEvents = filteredEvents
    .filter((event) => event.date >= todayStr && event.date <= fourWeeksFromNow)
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.time || "00:00").localeCompare(b.time || "00:00");
    });

  // Get event type config - with dynamic colors for appointments
  const getEventTypeConfig = (type, event = null) => {
    if (type === "appointment" && event?.assigned_to_id) {
      const collaboratorColor = getAppointmentColor(event.assigned_to_id);
      return {
        ...EVENT_TYPES.appointment,
        color: collaboratorColor.bg,
        badgeColor: collaboratorColor.badge,
        dotColor: collaboratorColor.dot
      };
    }
    return EVENT_TYPES[type] || EVENT_TYPES.court;
  };

  // Handle click on event - navigate to appropriate page
  const handleEventClick = (event) => {
    switch (event.type) {
      case "court":
        // Navigate to cases page with the case selected
        if (event.case_id) {
          navigate(`/cases?case=${event.case_id}`);
        } else {
          navigate("/cases");
        }
        break;
      case "task":
        // Navigate to tasks page with the task highlighted
        if (event.id) {
          navigate(`/tasks?task=${event.id}`);
        } else {
          navigate("/tasks");
        }
        break;
      case "appointment":
        // Navigate to appointments page
        if (event.id) {
          navigate(`/appointments?appointment=${event.id}`);
        } else {
          navigate("/appointments");
        }
        break;
      case "admin_note":
        // Navigate to admin notes page
        navigate("/admin-notes");
        break;
      default:
        break;
    }
  };

  const renderEventItem = (event) => {
    const config = getEventTypeConfig(event.type, event);
    const typeBorderColors = {
      court: "border-l-orange-400 bg-orange-50/50 hover:bg-orange-50",
      task: "border-l-blue-400 bg-blue-50/50 hover:bg-blue-50",
      appointment: "border-l-green-400 bg-green-50/50 hover:bg-green-50",
      admin_note: "border-l-purple-400 bg-purple-50/50 hover:bg-purple-50",
    };
    const borderStyle = typeBorderColors[event.type] || "border-l-gray-300 hover:bg-secondary/50";
    
    return (
      <div
        key={event.id}
        onClick={() => handleEventClick(event)}
        className={`flex items-center gap-2 py-1.5 px-2 rounded transition-colors cursor-pointer group border-l-[3px] ${borderStyle}`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleEventClick(event)}
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className={`w-1.5 h-1.5 rounded-full ${config.color} shrink-0`} />
          {/* Ora */}
          {event.time && (
            <span className="font-mono text-xs font-bold shrink-0">{event.time}</span>
          )}
          {/* Dosar asociat */}
          {event.numar_dosar && (
            <>
              <span className="text-muted-foreground shrink-0">|</span>
              <span className="font-mono text-xs text-muted-foreground shrink-0">{event.numar_dosar}</span>
            </>
          )}
          {/* Client */}
          {(event.type === "court" ? event.clients_display : event.client_name) && (
            <>
              <span className="text-muted-foreground shrink-0">|</span>
              <span className="text-sm truncate">
                {event.type === "court" ? event.clients_display : event.client_name}
              </span>
            </>
          )}
          {/* Avocat (doar la sarcini) */}
          {event.type === "task" && event.assigned_to_name && (
            <>
              <span className="text-muted-foreground shrink-0">|</span>
              <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">{event.assigned_to_name}</Badge>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă calendarul...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Calendar</h1>
          <p className="font-body text-muted-foreground mt-1">
            Vizualizează termene, sarcini și programări
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32" data-testid="view-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Calendar</SelectItem>
              <SelectItem value="list">Listă</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm font-medium text-muted-foreground">Afișează:</span>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="filter-court" 
                checked={showCourt} 
                onCheckedChange={setShowCourt}
              />
              <label htmlFor="filter-court" className="text-sm flex items-center gap-1 cursor-pointer">
                <span className={`w-3 h-3 rounded-full ${EVENT_TYPES.court.color}`}></span>
                Termene Judecată
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="filter-tasks" 
                checked={showTasks} 
                onCheckedChange={setShowTasks}
              />
              <label htmlFor="filter-tasks" className="text-sm flex items-center gap-1 cursor-pointer">
                <span className={`w-3 h-3 rounded-full ${EVENT_TYPES.task.color}`}></span>
                Sarcini
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="filter-appointments" 
                checked={showAppointments} 
                onCheckedChange={setShowAppointments}
              />
              <label htmlFor="filter-appointments" className="text-sm flex items-center gap-1 cursor-pointer">
                <span className={`w-3 h-3 rounded-full ${EVENT_TYPES.appointment.color}`}></span>
                Programări
              </label>
            </div>
            {user?.role === "admin" && (
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="filter-admin-notes" 
                  checked={showAdminNotes} 
                  onCheckedChange={setShowAdminNotes}
                />
                <label htmlFor="filter-admin-notes" className="text-sm flex items-center gap-1 cursor-pointer">
                  <span className={`w-3 h-3 rounded-full ${EVENT_TYPES.admin_note.color}`}></span>
                  Sarcini Personale
                </label>
              </div>
            )}
          </div>
          
          {/* Collaborator colors legend for appointments */}
          {showAppointments && getCollaboratorsLegend().length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <span className="text-sm font-medium text-muted-foreground mr-4">Programări per colaborator:</span>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {getCollaboratorsLegend().map((collab) => (
                  <div key={collab.id} className="flex items-center gap-1.5">
                    <span className={`w-3 h-3 rounded-full ${collab.color.bg}`}></span>
                    <span className="text-sm text-gray-600">{collab.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>{/* End Sticky */}

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Calendar Card */}
        <div className="xl:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                {format(currentMonth, "MMMM yyyy", { locale: ro })}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="prev-month"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCurrentMonth(new Date());
                    setSelectedDate(new Date());
                  }}
                  data-testid="today-btn"
                >
                  Astăzi
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="next-month"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                {/* Calendar picker */}
                <div className="shrink-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    locale={ro}
                    className="rounded-md border"
                    modifiers={{
                      hasEvent: (date) => {
                        const dateStr = format(date, "yyyy-MM-dd");
                        return datesWithEvents[dateStr]?.length > 0;
                      },
                      today: (date) => isToday(date),
                    }}
                    modifiersStyles={{
                      hasEvent: {
                        fontWeight: "bold",
                      },
                      today: {
                        backgroundColor: "hsl(var(--primary))",
                        color: "hsl(var(--primary-foreground))",
                        borderRadius: "50%",
                      },
                    }}
                    components={{
                      DayContent: ({ date }) => {
                        const dateStr = format(date, "yyyy-MM-dd");
                        const dayEvents = datesWithEvents[dateStr] || [];
                        const hasEvents = dayEvents.length > 0;
                        
                        const dots = [];
                        const seenTypes = new Set();
                        const seenCollaborators = new Set();
                        
                        dayEvents.forEach(event => {
                          if (event.type === "appointment" && event.assigned_to_id) {
                            if (!seenCollaborators.has(event.assigned_to_id)) {
                              seenCollaborators.add(event.assigned_to_id);
                              const collabColor = getAppointmentColor(event.assigned_to_id);
                              dots.push({ color: collabColor.bg, key: `apt-${event.assigned_to_id}` });
                            }
                          } else {
                            if (!seenTypes.has(event.type)) {
                              seenTypes.add(event.type);
                              dots.push({ color: getEventTypeConfig(event.type).color, key: event.type });
                            }
                          }
                        });
                        
                        return (
                          <div className="relative w-full h-full flex flex-col items-center justify-center">
                            <span>{format(date, "d")}</span>
                            {hasEvents && (
                              <div className="absolute bottom-0 flex gap-0.5">
                                {dots.slice(0, 4).map((dot) => (
                                  <span
                                    key={dot.key}
                                    className={`w-1.5 h-1.5 rounded-full ${dot.color}`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      },
                    }}
                  />
                </div>

                {/* Selected date events - fills the white space */}
                <div className="flex-1 min-w-0">
                  {selectedDate ? (
                    <>
                      <h3 className="font-heading text-sm font-semibold mb-3 text-accent">
                        {format(selectedDate, "EEEE, dd/MM/yyyy", { locale: ro })}
                      </h3>
                      {selectedDateEvents.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">Nu există evenimente</p>
                      ) : (
                        <div className="space-y-1.5">
                          {selectedDateEvents.map((event) => renderEventItem(event))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">Selectați o zi din calendar</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel: Upcoming Events only */}
        <div className="xl:col-span-1">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-accent" />
                Evenimente Viitoare (4 săpt.)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px] pr-4">
                {upcomingEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Nu există evenimente în următoarele 4 săptămâni
                  </p>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      const grouped = {};
                      upcomingEvents.forEach(event => {
                        const d = event.date;
                        if (!grouped[d]) grouped[d] = [];
                        grouped[d].push(event);
                      });
                      return Object.entries(grouped).map(([date, events]) => (
                        <div key={date}>
                          <div className="flex items-center gap-2 mb-1.5">
                            <p className="font-mono text-sm font-bold text-foreground">
                              {format(parseISO(date), "EEEE, dd/MM/yyyy", { locale: ro })}
                            </p>
                            <Badge variant="secondary" className="text-xs ml-auto">{events.length}</Badge>
                          </div>
                          <div className="ml-4 space-y-0.5">
                            {events.map((event) => renderEventItem(event))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
